import { HelpCircle } from 'lucide-react'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { RichText } from '@/components/RichText'
import YesNoInput from '@/components/inputs/YesNoInput'
import FreeFormInput from '@/components/inputs/FreeFormInput'
import SingleSelectInput from '@/components/inputs/SingleSelectInput'
import MultiSelectInput from '@/components/inputs/MultiSelectInput'
import DateInput from '@/components/inputs/DateInput'
import DateTimeInput from '@/components/inputs/DateTimeInput'
import UserReferenceInput from '@/components/inputs/UserReferenceInput'
import { FileInput } from './FileInput'
import type { Answers, FormField, FormPage } from '../types'
import { fieldVisible } from '../lib/form'

interface InputProps {
    field: FormField
    value: unknown
    onChange: (v: unknown) => void
    invalid?: boolean
    submissionId?: string
}

const asString = (v: unknown) => (typeof v === 'string' ? v : v === null || v === undefined ? '' : String(v))

/** One input control, chosen by the field's type. */
export function FieldInput({ field, value, onChange, invalid, submissionId }: InputProps) {
    const id = `f_${field.key}`
    switch (field.type) {
        case 'yes_no':
            return <YesNoInput id={id} value={asString(value)} onChange={onChange} />
        case 'text':
        case 'country':
            return <FreeFormInput id={id} value={asString(value)} onChange={onChange} />
        case 'textarea':
            return <FreeFormInput id={id} value={asString(value)} onChange={onChange} multiline />
        case 'single_select':
            return <SingleSelectInput id={id} value={asString(value)} options={field.options ?? []} onChange={onChange} />
        case 'multi_select':
            return <MultiSelectInput id={id} value={Array.isArray(value) ? (value as string[]) : []} options={field.options ?? []} onChange={onChange} />
        case 'date':
            return <DateInput id={id} value={asString(value)} onChange={onChange} />
        case 'user_reference':
            return <UserReferenceInput id={id} value={asString(value)} onChange={onChange} />
        case 'number':
            return (
                <Input
                    id={id}
                    type="number"
                    inputMode="decimal"
                    className="max-w-48"
                    aria-invalid={invalid}
                    value={asString(value)}
                    onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                />
            )
        case 'datetime':
            return <DateTimeInput id={id} value={asString(value)} invalid={invalid} onChange={onChange} />
        case 'file':
            return <FileInput id={id} fieldKey={field.key} value={value} onChange={onChange} submissionId={submissionId} invalid={invalid} />
        default:
            return null
    }
}

function HelpPopover({ text }: { text: string }) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex size-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-primary"
                    aria-label="More information"
                >
                    <HelpCircle className="size-4" />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-80 text-sm leading-relaxed text-muted-foreground" align="start">
                <RichText text={text} />
            </PopoverContent>
        </Popover>
    )
}

interface RendererProps {
    page: FormPage
    answers: Answers
    onChange: (key: string, value: unknown) => void
    /** Keys to flag as invalid (e.g. missing required after a failed Next). */
    invalidKeys?: string[]
    /** Enables file uploads; omitted where there is no submission (builder preview). */
    submissionId?: string
}

/** Renders one page of a form definition; visibility follows the answers. */
export default function FormRenderer({ page, answers, onChange, invalidKeys = [], submissionId }: RendererProps) {
    return (
        <FieldGroup>
            {page.fields.map((f) => {
                if (!fieldVisible(f, answers)) return null
                if (f.type === 'html') {
                    return (
                        <div key={f.key} className="rounded-lg bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
                            <RichText text={f.label} />
                            {f.help && <div className="mt-1"><RichText text={f.help} /></div>}
                        </div>
                    )
                }
                const invalid = invalidKeys.includes(f.key)
                return (
                    <Field key={f.key} id={`field-${f.key}`} data-invalid={invalid || undefined} className="scroll-mt-24">
                        <FieldLabel htmlFor={`f_${f.key}`} className="flex flex-wrap items-baseline gap-x-2">
                            <span>
                                <RichText text={f.label} />
                                {f.required && <span className="ml-1 font-semibold text-destructive">*</span>}
                            </span>
                            {f.help && <HelpPopover text={f.help} />}
                        </FieldLabel>
                        <FieldInput field={f} value={answers[f.key]} invalid={invalid} submissionId={submissionId} onChange={(v) => onChange(f.key, v)} />
                        {f.maxLength ? (
                            <FieldDescription className="text-xs">
                                {asString(answers[f.key]).length}/{f.maxLength}
                            </FieldDescription>
                        ) : null}
                        {invalid && <FieldError>This question is required.</FieldError>}
                    </Field>
                )
            })}
        </FieldGroup>
    )
}
